// @flow
import React, { useEffect, useState, useRef } from 'react'
import { styled, useTheme } from 'styled-components'
import {
	useSpring,
	animated,
	config,
	useTransition,
	to,
} from '@react-spring/web'
import {
	getShipHealth,
	useMissionStateSelector,
	getTotalPoints,
} from '../../../context'
import { usePrevious } from '../../../utility'
import { throttle } from 'lodash'
import VocalTrackAnimations from './VocalTrackAnimations'
import {
	getConnectedStudents,
	getMapPointInformation,
	getTotalCrewPoints,
} from '../../../context/missionState/missionStats'
import { ConnectedAudio } from '../../../sound/ConnectedMedia'
import {
	useJuniorPlusStateSelector,
	useVolumeOf,
} from '../../../context/customHooks'
import {
	ONE_SECOND,
	VOLUMES,
	cssPlayerHeightUnit,
} from '../../../utility/constants'
import { v4 as uuid } from 'uuid'
import { FaCheck } from 'react-icons/fa'

const HEALTH_BAR_WIDTH = 156

// The height of the bottom frame as a percentage of the total screen height
export const PERCENTAGE_OF_SCREEN_TAKEN_BY_BOTTOM_FRAME = 15.83

export default function BottomFrame(props: {|
	className?: string,
|}): React$Node {
	const { healthProgressSpring, shipColorSpring } = useShipHealthSpringStyles()
	const {
		totalPointsSpring,
		earnedPointsElement,
		earnedCrewPointsElement,
		audio,
	} = usePointSprings()

	return (
		<>
			<Svg
				xmlns="http://www.w3.org/2000/svg"
				viewBox="0 0 1920 182.88"
				{...props}>
				<defs>
					<rect width="100%" height="100%" id="ship_health_progress_bar_def" />
					<filter id="glow">
						<feGaussianBlur stdDeviation="5" result="coloredBlur" />
						<feMerge>
							<feMergeNode in="coloredBlur" />
							<feMergeNode in="SourceGraphic" />
						</feMerge>
					</filter>
				</defs>
				<clipPath id="ship_health_progress_bar_clip">
					<use xlinkHref="#ship_health_progress_bar_def" />
				</clipPath>
				<g id="Layer_2" data-name="Layer 2">
					<g id="Layer_3" data-name="Layer 3">
						<g id="Bottom_Frame" data-name="Bottom Frame">
							<polyline
								className="cls-1"
								points="340.29 162.47 374.45 128.31 1534.65 128.31 1579.1 172.76"
							/>

							<VocalTrackAnimations />

							<path
								className="cls-2"
								d="M308,176.78a2.55,2.55,0,0,1-1.77-.73l-44.95-45H172.89A87.06,87.06,0,1,1,36,23.92H0v159H369.94l6.1-6.1Z"
							/>
							<path
								className="cls-3"
								d="M1742.18,23.92a32,32,0,0,0-22.61,9.37L1606,146.88H314L200.43,33.29a32,32,0,0,0-22.61-9.37h-135A82.06,82.06,0,1,0,169.44,127.29l.12-.18a2.54,2.54,0,0,1,2.3-1h90.43a2.47,2.47,0,0,1,1.76.74l45,45h72l6.11-6.11h1145.7l17.21,17.21H1920v-159Z"
							/>
							<path
								className="cls-4"
								d="M101.6,168.17a87.06,87.06,0,0,0,71.29-37.08h88.36l44.95,45a2.55,2.55,0,0,0,1.77.73H376l5-5H309l-45-44.95a2.47,2.47,0,0,0-1.76-.74H171.86a2.54,2.54,0,0,0-2.3,1l-.12.18A82.06,82.06,0,1,1,42.77,23.92H36A87.05,87.05,0,0,0,101.6,168.17Z"
							/>
							<g>
								<rect
									className="cls-3"
									x="218.18"
									y="138.31"
									width="12.71"
									height="22.04"
									rx="4.32"
								/>
								<rect
									className="cls-3"
									x="237.94"
									y="138.31"
									width="12.71"
									height="22.04"
									rx="4.32"
								/>
							</g>
							<g>
								<path
									className="cls-2"
									d="M1920,158.23H1767.76a24.65,24.65,0,0,0-24.64,24.65H1920Z"
								/>
								<path
									className="cls-4"
									d="M1767.76,158.23H1920v-5H1767.76a29.68,29.68,0,0,0-29.64,29.65h5A24.65,24.65,0,0,1,1767.76,158.23Z"
								/>
							</g>
							<rect
								className="cls-5"
								x="1652.36"
								y="155"
								width="48.71"
								height="13.35"
								rx="5.44"
							/>
							<polygon
								className="cls-4"
								points="1617.22 165.67 302.78 165.67 285.57 182.88 1634.43 182.88 1617.22 165.67"
							/>
						</g>
						<g id="Points">
							<g>
								<rect
									className="cls-6"
									x="1645.97"
									y="58.86"
									width="241.35"
									height="76.27"
									rx="15.72"
								/>
								<path
									className="cls-2"
									d="M1871.6,63.86a10.72,10.72,0,0,1,10.72,10.72v44.83a10.72,10.72,0,0,1-10.72,10.72H1661.69A10.72,10.72,0,0,1,1651,119.41V74.58a10.72,10.72,0,0,1,10.72-10.72H1871.6m0-10H1661.69A20.74,20.74,0,0,0,1641,74.58v44.83a20.74,20.74,0,0,0,20.72,20.72H1871.6a20.74,20.74,0,0,0,20.72-20.72V74.58a20.74,20.74,0,0,0-20.72-20.72Z"
								/>
								{earnedPointsElement}
								{earnedCrewPointsElement}
								<animated.text
									x={TOTAL_POINTS_LOCATION.x}
									y={TOTAL_POINTS_LOCATION.y}
									className="font-game-headline"
									fontSize="20"
									fill="white">
									{totalPointsSpring.to(
										x =>
											`Pts: ${x.toLocaleString(undefined, {
												maximumFractionDigits: 0,
											})}`
									)}
								</animated.text>
							</g>
							<g className="cls-7">
								<image
									width="187"
									height="186"
									transform="translate(1631.52 52.32) scale(0.48)"
									xlinkHref="data:image/png;base64,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"
								/>
							</g>
							<circle className="cls-8" cx="1676.5" cy="97" r="8.54" />
						</g>
						<g id="Health">
							<path
								className="cls-2"
								d="M314.72,53.85H165.38a69.36,69.36,0,1,0,0,54.53H314.72a14.6,14.6,0,0,0,14.6-14.6V68.45A14.6,14.6,0,0,0,314.72,53.85Z"
							/>

							<Background
								x="149.11"
								y="68.21"
								width="165.86"
								rx="6.27"
								height="25.82"
							/>
							<animated.rect
								x="156.11"
								y="70.21"
								rx="6.27"
								height="20.82"
								style={{
									width: healthProgressSpring.percent.to(
										x => x * HEALTH_BAR_WIDTH
									),
									fill: healthProgressSpring.colorGrade.to(
										x => `hsl(${x}, 100%, 70%)`
									),
								}}
								clipPath="ship_health_progress_bar_clip"
							/>
							<animated.circle
								className="cls-6 ship-container"
								filter="url(#glow)"
								style={{
									stroke: shipColorSpring.containerStroke,
									strokeWidth: 2,
								}}
								cx="101.6"
								cy="81.12"
								r="57.19"
							/>

							<g>
								<g className="cls-7"></g>
								<animated.path
									filter="url(#glow)"
									className="cls-8 ship"
									style={{ fill: shipColorSpring.iconFill }}
									d="M131.8,57l-3.1-6.32-3.87-4.78-5-4.12-5.42-3.23-6.32-1.81-6.46-.57-6.47.57-6.32,1.81-5.42,3.23-5,4.12-3.87,4.78L71.39,57l-1.8,6.19-.65,7.61,5.16,45.55,2.66,3.92a12,12,0,0,0,8.36,5.19l4.34.6h24.27l4.34-.6a12,12,0,0,0,8.36-5.19l2.66-3.92,5.16-45.55-.65-7.61ZM97.59,116.22l-9,.65-3.87,1A6.32,6.32,0,0,1,80.52,115l-.22-.37v-3.48l1.8-3.1,1.55-4.9,5.29,4.26,1.42.12.13,1-.9.9.64,2.19,3.23,1.68,4.13.13Zm25.3-1.55-.22.37a6.29,6.29,0,0,1-4.16,2.86l-3.87-1-9-.65v-2.71l4.13-.13L113,111.7l.65-2.19-.91-.9.13-1,1.42-.12,5.29-4.26,1.55,4.9,1.8,3.1Z"
								/>
								<polygon
									className="cls-9"
									points="107.1 91.19 107.1 78 120.29 78 120.29 66.98 107.1 66.98 107.1 53.79 96.09 53.79 96.09 66.98 82.9 66.98 82.9 78 96.09 78 96.09 91.19 107.1 91.19"
								/>
							</g>
						</g>
					</g>
				</g>
			</Svg>
			<MapPointsStats />
			{audio}
		</>
	)
}

const TOTAL_POINTS_LOCATION = { x: 1693, y: 104.54 }

const Background = styled.rect`
	fill: ${({ theme }) => theme.base3};
`

const Svg = styled.svg`
	overflow: visible;
	.cls-1 {
		fill: none;
		stroke: aqua;
		stroke-linecap: round;
		stroke-linejoin: round;
		stroke-width: 5px;
	}

	.cls-2 {
		fill: #5d568c;
	}

	.cls-3 {
		fill: #364984;
	}

	.cls-4 {
		fill: #14163d;
	}

	.cls-5 {
		fill: #29326d;
	}

	.cls-6 {
		fill: #0f193c;
	}

	.cls-7 {
		opacity: 0.5;
	}

	.cls-8 {
		fill: aqua;
	}

	.cls-9 {
		fill: #fff;
	}
`

/**
 * Uses react-spring to create animations for the different components of the ship health portion of the frame
 * @returns { shipColorSpring.containerStroke: ReactSpringAnimation } When ship health goes up/down, the container around the ship icon pulses color
 * @returns { shipColorSpring.iconFill: ReactSpringAnimation } When ship health goes up/down, the ship icon pulses color
 * @returns { healthProgressSpring.percent: ReactSpringAnimation } Progress bar animation when ship health changes
 * @returns { healthProgressSpring.colorGrade: ReactSpringAnimation } Progress bar color change when ship health changes.
 */
const useShipHealthSpringStyles = () => {
	const shipHealth = useMissionStateSelector(getShipHealth) || 0
	const [healthBarBlink, setHealthBarBlink] = useState()
	const previousShipHealth = usePrevious(shipHealth) || shipHealth
	const loop = useRef(0)
	useEffect(() => {
		if (previousShipHealth > shipHealth) {
			setHealthBarBlink(-1)
			loop.current = 0
		} else if (previousShipHealth < shipHealth) {
			setHealthBarBlink(1)
			loop.current = 0
		}
	}, [previousShipHealth, shipHealth])
	const getColorGrade = healthValue =>
		healthValue > 60 ? 100 : healthValue < 35 ? 0 : 35

	const healthProgressSpring = useSpring({
		from: {
			percent: previousShipHealth / 100,
			colorGrade: getColorGrade(previousShipHealth),
		},
		to: {
			percent: shipHealth / 100,
			colorGrade: getColorGrade(shipHealth),
		},
		config: config.molasses,
	})

	const [shipColorSpring] = useSpring(() => {
		const toColor =
			healthBarBlink === -1
				? '#ff2524'
				: healthBarBlink === 1
				? '#99ff99'
				: null
		loop.current = 0
		return {
			reset: !toColor,
			config: {
				mass: 0.5,
				tension: 160,
				friction: 10,
			},
			loop: () => {
				if (!toColor) return false
				if (loop.current++ > 5) {
					return false
				}
				return { reverse: true }
			},
			onRest: () => {
				if (loop.current === 5) {
					setHealthBarBlink(null)
				}
			},
			to: {
				containerStroke: toColor || '#0f193c',
				iconFill: toColor || 'aqua',
			},
			from: {
				containerStroke: '#0f193c',
				iconFill: 'aqua',
			},
		}
	}, [healthBarBlink])
	return {
		shipColorSpring,
		healthProgressSpring,
	}
}

const LOSE_CREW_POINTS_AUDIO =
	'https://resources-cdn.mission.io/simulations/soundEffects/loseCrewPoints.wav'
const GAIN_CREW_POINTS_AUDIO =
	'https://resources-cdn.mission.io/simulations/soundEffects/gainCrewPoints.mp3'

/**
 * Returns the length of the text for the crew points earned
 * @param {number} points
 * @returns
 */
const getTextLength = points => {
	const length = String(points).length
	return 48 + length * 60
}

/**
 * Uses react-spring to create animations for the points portion of the frame
 * @returns { totalPointsSpring: ReactSpringAnimation } The total points to display, will animation as points go up and down.
 * @returns { earnedPointsElement: JSX.Element } An element which will display earned points as they occur.
 *
 */
const usePointSprings = () => {
	const points = useMissionStateSelector(getTotalPoints)
	const crewPoints = useMissionStateSelector(getTotalCrewPoints)
	const previousCrewPoints = useRef(crewPoints)
	const previousPoints = useRef(points)
	const [recentlyEarnedPoints, setRecentlyEarnedPoints] = useState([])
	const [recentlyEarnedCrewPoints, setRecentlyEarnedCrewPoints] = useState([])
	const effectTrackVolume = useVolumeOf(VOLUMES.EFFECTS, false)
	const successColor = useTheme()?.success ?? 'green'

	const connectedStudents =
		Object.keys(useMissionStateSelector(getConnectedStudents)).length || 1

	const throttled = useRef(
		throttle(value => {
			if (value !== previousPoints.current) {
				const recentlyEarned = value - previousPoints.current
				setRecentlyEarnedPoints(state => [
					...state,
					{ points: recentlyEarned, time: Date.now() },
				])
			}
			previousPoints.current = value
		}, 1000)
	)
	useEffect(() => {
		throttled.current(points)
	}, [points])

	const throttledCrewPoints = useRef(
		throttle(value => {
			if (value !== previousCrewPoints.current) {
				const recentlyEarned = value - previousCrewPoints.current
				setRecentlyEarnedCrewPoints(state => [
					...state,
					{
						points: recentlyEarned,
						time: Date.now(),
						audio:
							recentlyEarned > 0
								? GAIN_CREW_POINTS_AUDIO
								: LOSE_CREW_POINTS_AUDIO,
					},
				])
			}
			previousCrewPoints.current = value
		}, 1000)
	)
	useEffect(() => {
		throttledCrewPoints.current(crewPoints)
	}, [crewPoints])

	const { number } = useSpring({
		number: points + connectedStudents * crewPoints,
	})
	const transitions = useTransition(recentlyEarnedPoints, {
		key: item => item.time,
		from: {
			x: TOTAL_POINTS_LOCATION.x + 58,
			y: TOTAL_POINTS_LOCATION.y + 10,
			fontSize: 20,
			fillOpacity: 1,
		},
		enter: {
			x: TOTAL_POINTS_LOCATION.x,
			y: -200,
			fontSize: 36,
			fillOpacity: 1,
		},
		leave: { fillOpacity: 0 },
		config: { duration: 800 },
		onRest: () => {
			setRecentlyEarnedPoints([])
		},
	})

	const crewPointTransitions = useTransition(recentlyEarnedCrewPoints, {
		key: item => item.time,
		from: {
			x: TOTAL_POINTS_LOCATION.x,
			y: TOTAL_POINTS_LOCATION.y,
			subtitleX: TOTAL_POINTS_LOCATION.x,
			fontSize: 0,
			textFontSize: 0,
			textLength: 0,
			subtitleLength: 0,
			fillOpacity: 1,
		},
		enter: item => {
			const textLength = getTextLength(item.points)
			const subtitleLength = 228
			let subtitleX = TOTAL_POINTS_LOCATION.x - 350
			let x = subtitleX
			if (textLength > subtitleLength) {
				const overflow = textLength - subtitleLength
				x -= overflow / 2
			}
			return [
				{
					x,
					y: TOTAL_POINTS_LOCATION.y - 100,
					subtitleX,
					textLength,
					subtitleLength,
					fontSize: 75,
					textFontSize: 28,
					fillOpacity: 1,
					config: { duration: 1000 },
				},
				{
					x,
					y: TOTAL_POINTS_LOCATION.y - 700,
					subtitleX,
					textLength,
					subtitleLength,
					fillOpacity: 1,
					config: { duration: 3000 },
				},
			]
		},
		leave: { fillOpacity: 0, config: { duration: 500 } },
		onRest: (_, _2, item) => {
			setRecentlyEarnedCrewPoints(pointsAnimation =>
				pointsAnimation.filter(({ time }) => time > item.time)
			)
		},
	})

	return {
		earnedPointsElement: transitions((styles, item) => {
			return (
				item && (
					<animated.text
						className="font-game-headline"
						{...styles}
						fill={item.points > 0 ? successColor : 'red'}>
						{item.points > 0 ? '+' : ''}
						{item.points}
					</animated.text>
				)
			)
		}),
		earnedCrewPointsElement: crewPointTransitions(
			({ subtitleLength, subtitleX, ...styles }, item) => {
				return (
					item && (
						<g>
							<animated.text
								className="font-game-headline"
								{...styles}
								fill={item.points > 0 ? successColor : 'red'}
								fontWeight="bold">
								{item.points > 0 ? '+' : ''}
								{item.points}
							</animated.text>
							<animated.text
								className="font-game-headline"
								{...styles}
								x={subtitleX}
								textLength={subtitleLength}
								y={to(
									[styles.y, styles.textFontSize],
									(y, fontSize) => y + fontSize
								)}
								font-weight="bold"
								fontSize={styles.textFontSize}
								fill={item.points > 0 ? successColor : 'red'}
								fontWeight="bold">
								PER PERSON
							</animated.text>
						</g>
					)
				)
			}
		),
		audio: recentlyEarnedCrewPoints.map(({ audio, time }) => (
			<ConnectedAudio key={time} src={audio} volume={effectTrackVolume} />
		)),
		totalPointsSpring: number,
	}
}

const FADE_IN_DURATION = 1 * ONE_SECOND
const VISIBLE_DURATION = 2 * ONE_SECOND
const FADE_OUT_DURATION = 1 * ONE_SECOND

/**
 * MapPointsStats - a react components which monitors the given points stats of maps and reports them when changed
 *
 * @return {React$Node}
 */
function MapPointsStats() {
	const currentMapPoints = useJuniorPlusStateSelector(getMapPointInformation)

	// using ref because it is needed in an effect which should not be updated when this value changes
	const previousMapPoints = useRef(currentMapPoints)
	previousMapPoints.current = usePrevious(currentMapPoints) ?? currentMapPoints

	const [isFadingIn, setIsFadingIn] = useState(true)

	const [pointsToSee, setPointsToSee] = useState<
		{
			key: string,
			mapId: string,
			missedPoints: number,
			missedMapObjects: number,
		}[]
	>([])

	useEffect(() => {
		const mapsNeedToUpdate = new Set()
		Object.keys(currentMapPoints).forEach(mapId => {
			const current = currentMapPoints[mapId]
			const previous = previousMapPoints.current[mapId]

			if (
				!previous ||
				current.relevantObjectsStillAvailableForScan <
					previous.relevantObjectsStillAvailableForScan ||
				current.pointsStillAvailable < previous.pointsStillAvailable
			) {
				mapsNeedToUpdate.add(mapId)
			}
		})

		if (!mapsNeedToUpdate.size) {
			return
		}

		setPointsToSee(previousPointsToSee => {
			const updatedPointsToSee = [...previousPointsToSee]

			mapsNeedToUpdate.forEach(mapId => {
				const current = currentMapPoints[mapId]
				updatedPointsToSee.push({
					key: uuid(),
					mapId,
					missedPoints: current.pointsStillAvailable,
					missedMapObjects: current.relevantObjectsStillAvailableForScan,
				})
			})

			return updatedPointsToSee
		})
	}, [currentMapPoints])

	const current_element = pointsToSee[0]
	const current_element_id = current_element?.key

	const [fadeInStyles] = useSpring(
		() => ({
			from: { opacity: 0 },
			to: { opacity: 1 },
			pause: current_element_id == null,
			config: {
				duration: FADE_IN_DURATION,
			},
			onStart: () => {
				setIsFadingIn(true)
			},
			reset: true,
		}),
		[current_element_id]
	)

	const [fadeOutStyles] = useSpring(
		() => ({
			from: { opacity: 1 },
			to: { opacity: 0 },
			pause: current_element_id == null,
			delay: FADE_IN_DURATION + VISIBLE_DURATION,
			config: {
				duration: FADE_OUT_DURATION,
			},
			onStart: () => {
				setIsFadingIn(false)
			},
			onRest: () => {
				if (current_element_id == null) {
					return
				}
				setPointsToSee(previousPointsToSee => {
					// remove current stats being animated, this will cause the next stats animation to start if there is one
					const updated = [...previousPointsToSee].filter(
						({ key }) => key !== current_element_id
					)
					return updated
				})
			},
			reset: true,
		}),
		[current_element_id]
	)

	if (!current_element) {
		return null
	}

	return (
		<StyledInformation style={isFadingIn ? fadeInStyles : fadeOutStyles}>
			<Box>
				{current_element.missedMapObjects === 0 &&
				current_element.missedPoints === 0 ? (
					<Good>
						<FaCheck />
						All essential data collected
					</Good>
				) : (
					<Bad>
						<div>
							Unexamined essential data: {current_element.missedMapObjects}
						</div>
						<div>Missed {current_element.missedPoints} points per student</div>
					</Bad>
				)}
			</Box>
		</StyledInformation>
	)
}

const Good = styled.div`
	color: ${({ theme }) => theme.success};
	display: flex;
	flex-direction: row;
	gap: ${({ theme }) => theme.spacing};
`

const Bad = styled.div`
	color: ${({ theme }) => theme.error};
`

const Box = styled.div.attrs({ className: 'font-game-headline' })`
	background-color: #0002;
	padding: ${({ theme }) => theme.spacing};
	border-radius: 50%;
	box-shadow: 0px 0px 30px #0006;
`

const StyledInformation = styled(animated.div)`
	font-size: ${cssPlayerHeightUnit(5)};
	width: 100%;
	height: 58%;
	padding-top: 42%;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
`
